import { GridColDef } from '@mui/x-data-grid-premium';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ButtonCellComponent,
  EmployeeCellComponent,
} from '~/components/SolaForceDataGrid';
import { FTEReportData } from '~/data/reports';
import { useReportNavigateToView } from '~/scenes/PortalModules/Analytics/common';

type FTEReportEntry = {
  employeeId: number;
  firstName: string;
  lastName: string;
  employeeNumber: string;
  organizationUnit: string;
  organizationUnitNumber: string;
  grossWorkingDays: number;
  numberOfLongAbsences: number;
  reducedHoursInDays: number;
  netFTE: number;
  hasAccount: boolean;
};

type PropsType = {
  data: FTEReportData[];
};

export const useFTEReport = (props: PropsType) => {
  const { data } = props;

  const { goToEmployeeBasicDataView } = useReportNavigateToView();

  const { t } = useTranslation();

  const rows = useMemo(() => {
    return data.map((entry): FTEReportEntry => {
      const {
        fEmpId,
        fEmpFirstName,
        fEmpLastName,
        fEmpNumber,
        fEmpUnitName,
        fEmpUnitNumber,
        fEmpWorkingHours: {
          grossWorkingDays,
          numberOfLongAbsences,
          reducedHoursInDays,
          netFTE,
        },
        fEmpHasAccount,
      } = entry;

      return {
        employeeId: fEmpId,
        firstName: fEmpFirstName,
        lastName: fEmpLastName,
        employeeNumber: fEmpNumber,
        organizationUnit: fEmpUnitName,
        organizationUnitNumber: fEmpUnitNumber,
        grossWorkingDays,
        numberOfLongAbsences,
        reducedHoursInDays,
        netFTE,
        hasAccount: fEmpHasAccount,
      };
    });
  }, [data]);

  const { columnConfigs } = useMemo(() => {
    const getColumnWidthByHeaderLength = (
      columnHeader: string,
      minWidth: number,
    ) => {
      const headerColumnWidth = columnHeader.length * 9;

      return headerColumnWidth > minWidth ? headerColumnWidth : minWidth;
    };

    const columnConfigs: GridColDef<FTEReportEntry>[] = [
      {
        field: 'employeeId',
        headerName: t('laPicture'),
        filterable: false,
        sortable: false,
        disableExport: true,
        renderCell: (params) => {
          return (
            <EmployeeCellComponent
              {...params}
              pictureOnly={true}
              onClick={() => {
                goToEmployeeBasicDataView(params.row.employeeId);
              }}
            />
          );
        },
        minWidth: 60,
      },
      {
        field: 'firstName',
        headerName: t('laFirstName'),
        hideable: false,
        renderCell: (params) => {
          return (
            <ButtonCellComponent
              {...params}
              onClick={() => {
                goToEmployeeBasicDataView(params.row.employeeId);
              }}
            />
          );
        },
        minWidth: 90,
      },
      {
        field: 'lastName',
        headerName: t('laLastName'),
        hideable: false,
        renderCell: (params) => {
          return (
            <ButtonCellComponent
              {...params}
              onClick={() => {
                goToEmployeeBasicDataView(params.row.employeeId);
              }}
            />
          );
        },
        minWidth: 110,
      },
      {
        field: 'employeeNumber',
        headerName: t('laEmpId'),
      },
      {
        field: 'organizationUnit',
        headerName: t('laOrgUnit'),
        minWidth: 180,
      },
      { field: 'organizationUnitNumber', headerName: t('laUnitNumber') },
      {
        field: 'grossWorkingDays',
        headerName: t('laGrossWorkingDays'),
        type: 'number',
        width: getColumnWidthByHeaderLength(t('laGrossWorkingDays'), 80),
      },
      {
        field: 'numberOfLongAbsences',
        headerName: t('laLongAbsences'),
        type: 'number',
        width: getColumnWidthByHeaderLength(t('laLongAbsences'), 80),
      },
      {
        field: 'reducedHoursInDays',
        headerName: t('laReducedHours'),
        type: 'number',
        width: getColumnWidthByHeaderLength(t('laReducedHours'), 80),
      },
      {
        field: 'netFTE',
        headerName: t('laNetFTE'),
        type: 'number',
        width: getColumnWidthByHeaderLength(t('laNetFTE'), 80),
      },
    ];

    return {
      columnConfigs,
    };
  }, [t, goToEmployeeBasicDataView]);

  return {
    table: {
      columnConfigs,
      rows,
    },
  };
};
